import { useEffect, useState, useRef } from "react";
import {
  BookOpen,
  FileSpreadsheet,
  Compass,
  Building2,
  Users,
  ClipboardList,
  ShieldCheck,
  TrendingUp,
  BedDouble,
  UserCheck,
  FileText,
  AlertTriangle,
  Download,
  Sparkles,
  Table as TableIcon,
  HelpCircle,
  Library,
  ChevronDown,
  LogIn,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link } from "react-router-dom";

const SECTIONS = [
  { id: "introduzione", label: "Introduzione", icon: Compass },
  { id: "struttura", label: "Struttura organizzativa", icon: Building2 },
  { id: "ruoli", label: "Ruoli e permessi", icon: Users },
  { id: "strutture", label: "Strutture & Anagrafica", icon: Building2 },
  { id: "import", label: "Import massivo da Excel", icon: FileSpreadsheet },
  { id: "controlli", label: "Controlli giornalieri", icon: ClipboardList },
  { id: "audit", label: "Ispezioni Capo Area", icon: ShieldCheck },
  { id: "performance", label: "Performance RM", icon: TrendingUp },
  { id: "housekeeping", label: "Produttività HK", icon: BedDouble },
  { id: "staff", label: "Valutazione Staff", icon: UserCheck },
  { id: "relazioni", label: "Relazioni mensili", icon: FileText },
  { id: "daily", label: "Login giornaliero (DLA)", icon: LogIn },
  { id: "alert", label: "Sistema Alert", icon: AlertTriangle },
  { id: "report", label: "Report PDF & Excel", icon: Download },
  { id: "ai", label: "AI Assistant", icon: Sparkles },
  { id: "matrice", label: "Matrice permessi", icon: TableIcon },
  { id: "faq", label: "FAQ", icon: HelpCircle },
  { id: "glossario", label: "Glossario", icon: Library },
];

function SectionHeading({ icon: Icon, n, title }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="h-9 w-9 rounded-xl bg-lavender-100/80 dark:bg-lavender-900/40 flex items-center justify-center text-lavender-700 dark:text-lavender-300">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Capitolo {n}</div>
        <h2 className="font-display text-2xl tracking-tight font-medium">{title}</h2>
      </div>
    </div>
  );
}

function Card({ children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-lavender-200/60 dark:border-lavender-800/30 bg-background/50 dark:bg-slate-900/30 p-5 ${className}`}>
      {children}
    </div>
  );
}

function ProseList({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5 text-sm leading-[1.75] text-foreground/85">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

function OrderedSteps({ items }) {
  return (
    <ol className="list-decimal pl-5 space-y-1.5 text-sm leading-[1.75] text-foreground/85">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ol>
  );
}

function PermessiBlock({ rows }) {
  return (
    <div className="mt-4 rounded-xl border border-border/60 bg-accent/20 p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Chi può fare cosa</div>
      <ul className="space-y-1.5 text-sm">
        {rows.map(([role, action], i) => (
          <li key={i} className="flex gap-3">
            <span className="text-lavender-700 dark:text-lavender-300 font-medium min-w-[140px]">{role}</span>
            <span className="text-foreground/85">{action}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function useActiveSection() {
  const [active, setActive] = useState(SECTIONS[0].id);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: [0, 0.2, 0.5, 1] }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return active;
}

export default function Guida() {
  const active = useActiveSection();
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const mainRef = useRef(null);

  const scrollTo = (id) => {
    setMobileTocOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const buildDate = new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="ih-anim-in space-y-6" data-testid="guida-page">
      {/* Header */}
      <div>
        <nav className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
          <Link to="/" className="hover:text-lavender-600 transition-colors">Dashboard</Link>
          <span>/</span>
          <span className="text-foreground/80">Guida</span>
        </nav>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-[280px]">
            <div className="flex items-center gap-3 mb-1">
              <BookOpen className="h-5 w-5 text-lavender-500" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Manuale operativo</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light">Guida &amp; Funzionalità</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-2xl">
              Manuale operativo IH AuditFlow per Invest Hospitality. Tutto quello che serve a usare la piattaforma in autonomia, per modulo e per ruolo.
            </p>
          </div>
          <button
            type="button"
            data-testid="guida-download-pdf-btn"
            onClick={async () => {
              try {
                const res = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/guida/pdf`,
                  { credentials: "include" }
                );
                if (!res.ok) throw new Error("Errore download PDF");
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "IH_AuditFlow_Guida.pdf";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                const { toast } = await import("sonner");
                toast.success("Guida PDF scaricata");
              } catch (e) {
                const { toast } = await import("sonner");
                toast.error("Errore nel download della Guida PDF");
              }
            }}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border border-lavender-300 bg-lavender-50 dark:bg-lavender-950/40 text-lavender-700 dark:text-lavender-200 hover:bg-lavender-100 dark:hover:bg-lavender-900/50 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Scarica Guida PDF</span>
          </button>
        </div>
      </div>

      {/* Mobile TOC */}
      <div className="lg:hidden">
        <button
          data-testid="guida-toc-mobile-toggle"
          onClick={() => setMobileTocOpen((v) => !v)}
          className="w-full flex items-center justify-between rounded-xl border border-border/60 bg-background/50 px-4 py-3 text-sm"
        >
          <span className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-lavender-500" />
            Indice del manuale
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${mobileTocOpen ? "rotate-180" : ""}`} />
        </button>
        {mobileTocOpen && (
          <div className="mt-2 rounded-xl border border-border/60 bg-background/60 backdrop-blur p-2 max-h-72 overflow-y-auto">
            {SECTIONS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`w-full text-left text-sm px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-accent/50 transition-colors ${active === s.id ? "bg-lavender-100/70 dark:bg-lavender-900/30 text-lavender-700 dark:text-lavender-300" : ""}`}
              >
                <span className="text-[10px] text-muted-foreground w-5">{String(i + 1).padStart(2, "0")}</span>
                <s.icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{s.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-8">
        {/* Sticky TOC desktop */}
        <aside className="hidden lg:block">
          <div className="sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto pr-2 ih-glass rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3 px-1">Indice</div>
            <nav className="space-y-0.5">
              {SECTIONS.map((s, i) => {
                const Icon = s.icon;
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    data-testid={`toc-${s.id}`}
                    className={`w-full text-left text-[13px] px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${isActive ? "bg-lavender-100/80 dark:bg-lavender-900/30 text-lavender-700 dark:text-lavender-300 font-medium" : "text-muted-foreground hover:text-foreground hover:bg-accent/40"}`}
                  >
                    <span className="text-[10px] tabular-nums opacity-60 w-5">{String(i + 1).padStart(2, "0")}</span>
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{s.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main ref={mainRef} className="max-w-[820px] space-y-14 leading-[1.75]">
          {/* 1 */}
          <section id="introduzione">
            <SectionHeading n="01" icon={Compass} title="Introduzione" />
            <div className="space-y-5 text-[15px] text-foreground/85">
              <div>
                <h3 className="font-medium text-foreground mb-1.5">Cos'è IH AuditFlow</h3>
                <p>IH AuditFlow è la piattaforma interna di Invest Hospitality dedicata al controllo qualità operativo, al monitoraggio degli standard, alle ispezioni periodiche, al controllo delle performance manageriali e ai KPI del personale operante nelle strutture alberghiere del gruppo.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1.5">Cosa NON fa</h3>
                <p>IH AuditFlow <strong>non gestisce manutenzioni, guasti o ticket tecnici</strong>. Le problematiche tecniche continuano a essere gestite tramite la piattaforma <strong>HostFix Pro</strong>. IH AuditFlow è esclusivamente focalizzato sulla qualità percepita, la conformità agli standard e le performance del personale.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1.5">Cosa fa, in sintesi</h3>
                <ProseList items={[
                  "Verifica giornaliera degli standard di pulizia, ordine e qualità percepita",
                  "Ispezioni Capo Area programmate ogni 60 giorni",
                  "Monitoraggio della performance dei Resident Manager",
                  "Misura della produttività Housekeeping (camere/ora)",
                  "Raccolta delle relazioni mensili obbligatorie",
                  "Valutazione del personale con questionari configurabili",
                  "Generazione automatica di alert su anomalie operative",
                  "Report PDF/Excel per management e direzione",
                  "Assistente AI per analisi e riepiloghi corporate",
                ]} />
              </div>
            </div>
          </section>

          {/* 2 */}
          <section id="struttura">
            <SectionHeading n="02" icon={Building2} title="Struttura organizzativa" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p>La piattaforma rispecchia l'organizzazione di Invest Hospitality:</p>
              <Card>
                <code className="text-sm font-mono text-lavender-700 dark:text-lavender-300">Invest Hospitality → Strutture → Reparti → Utenti</code>
              </Card>
              <div>
                <p className="mb-2">Ogni struttura (hotel) contiene:</p>
                <ProseList items={[
                  "Nome, indirizzo, città",
                  "Numero camere e categoria stelle",
                  "Resident Manager assegnato",
                  "Capo Area assegnato",
                  "Storico ispezioni, punteggio qualità, KPI storici",
                ]} />
              </div>
              <p>I reparti standard sono: <strong>Housekeeping, Front Office, F&amp;B, Direzione</strong>.</p>
            </div>
          </section>

          {/* 3 */}
          <section id="ruoli">
            <SectionHeading n="03" icon={Users} title="Ruoli e permessi (panoramica)" />
            <p className="text-[15px] text-foreground/85 mb-4">La piattaforma prevede 6 ruoli con livelli di accesso differenti:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["Super Admin", "Gestione totale, configurazione, dashboard corporate, esportazioni, gestione utenti"],
                ["Capo Area", "Gestisce più hotel in cluster geografico, esegue ispezioni ogni 60gg, confronta strutture, trend"],
                ["Resident Manager (RM)", "Riceve task quotidiane, esegue controlli, compila verifiche, scrive relazioni mensili, consulta KPI"],
                ["Housekeeping Manager", "Controlli pulizia, standard camere, produttività del reparto"],
                ["Front Office Manager", "Controllo reception, aree ospiti, standard front office"],
                ["Utente Sola Lettura", "Accesso in sola visualizzazione, nessuna scrittura"],
              ].map(([r, d]) => (
                <Card key={r}>
                  <div className="text-sm font-medium text-lavender-700 dark:text-lavender-300">{r}</div>
                  <div className="text-sm text-foreground/75 mt-1">{d}</div>
                </Card>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4">Per la matrice dettagliata "Chi può fare cosa" vedere la sezione dedicata in fondo.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <Card className="border-l-4 border-l-lavender-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-700 dark:text-lavender-300 mb-1.5">Disattivazione utenti</div>
                <p className="text-sm">Il super admin può <strong>disattivare</strong> un utente (soft delete): non potrà più loggarsi e sarà escluso da liste/KPI, ma tutti i dati restano. Reversibile dal toggle "Disattivati".</p>
              </Card>
              <Card className="border-l-4 border-l-rose-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400 mb-1.5">Eliminazione definitiva</div>
                <p className="text-sm">Per casi straordinari: solo da tab "Disattivati", con doppia conferma cognome. <strong className="text-rose-600">Irreversibile.</strong> Cancella assenze/notifiche/sessioni AI, sgancia hotel (NULL), marca task storiche come "Utente eliminato" (storico hotel preservato).</p>
              </Card>
              <Card className="border-l-4 border-l-amber-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400 mb-1.5">Reset utente</div>
                <p className="text-sm">Azzera le performance storiche di un utente senza disattivarlo: marca tutte le sue task come "resettata_admin", così KPI/classifiche ripartono da zero. <strong>Una task appartiene all'utente</strong> se è assegnata esplicitamente, oppure è stata firmata da lui, oppure è dell'hotel del suo cluster col ruolo coerente. Le task future continuano normalmente.</p>
              </Card>
            </div>
          </section>

          {/* 4 */}
          <section id="strutture">
            <SectionHeading n="04" icon={Building2} title="Strutture & Anagrafica" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa il modulo</strong>: gestione del portfolio hotel con anagrafica completa, assegnazioni manageriali, punteggio qualità corrente e sparkline di trend 30 giorni.</p>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Come si usa</h4>
                <OrderedSteps items={[
                  "Vai in Strutture dal menu laterale",
                  "Visualizza la griglia con tutte le strutture di tua competenza (filtraggio automatico per ruolo)",
                  "Clicca una struttura per accedere al dettaglio completo: panoramica, controlli oggi, storico ispezioni, performance team, produttività HK, relazioni mensili",
                  "Da /strutture/:id puoi esportare il report mensile in PDF o Excel direttamente dall'header",
                ]} />
              </div>
              <PermessiBlock rows={[
                ["Super Admin", "Creazione, modifica, soft delete di hotel"],
                ["Capo Area", "Visualizzazione hotel del proprio cluster"],
                ["Resident Manager", "Visualizzazione del proprio hotel"],
                ["HK / FO Manager", "Visualizzazione del proprio hotel"],
                ["Read-only", "Visualizzazione"],
              ]} />
            </div>
          </section>

          {/* 5 — Import Excel */}
          <section id="import">
            <SectionHeading n="05" icon={FileSpreadsheet} title="Importazione massiva da Excel" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: permette di caricare in blocco strutture (hotel) e dipendenti (staff) tramite file Excel pre-formattato, evitando l'inserimento manuale di centinaia di record.</p>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Quando usarla</h4>
                <ProseList items={[
                  "Onboarding iniziale di un nuovo gruppo di hotel",
                  "Caricamento massivo di dipendenti dopo l'apertura di una nuova struttura",
                  "Aggiornamento periodico di dati anagrafici (es. nuove assunzioni)",
                ]} />
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Come si usa</h4>
                <OrderedSteps items={[
                  "Vai in Strutture (per hotel) o Staff (per dipendenti)",
                  'Clicca il pulsante "Importa da Excel" in alto a destra',
                  "Step 1 — Setup: scarica il template .xlsx pre-formattato. Compilalo rispettando i nomi delle colonne e i valori ammessi (dropdown già impostati su categoria stelle e dipartimento)",
                  "Carica il file compilato e scegli la modalità: Solo nuovi (salta duplicati) oppure Aggiorna esistenti (upsert)",
                  'Clicca "Verifica file": il sistema esegue un\'anteprima riga per riga con stato Da creare / Da aggiornare / Saltato / Errore',
                  'Controlla l\'anteprima e clicca "Conferma importazione"',
                  "Step 3 — Esito: vedi il riepilogo finale con il numero di record creati, aggiornati e in errore",
                ]} />
              </div>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Validazioni automatiche</div>
                <ProseList items={[
                  "Hotel: nome univoco (chiave per match in modalità 'Aggiorna'), categoria stelle solo 3/4/5, email RM/Capo Area opzionali ma se presenti devono esistere a sistema con il ruolo corretto",
                  "Staff: chiave match (nome, cognome, hotel), dipartimento da elenco fisso (housekeeping/front_office/f&b/direzione), hotel deve esistere nel sistema, responsabile_email opzionale ma deve essere un utente esistente, eta opzionale (intero 0-99)",
                  "Camere per hotel: solo Super Admin. Da tab Camere su /strutture/:id scarica template Excel con colonne numero (obbligatorio, univoco per hotel), piano, tipologia, note. Upsert chiave 'numero' case-insensitive.",
                  "Camere multi-hotel: solo Super Admin. Da /strutture bottone 'Importa camere (multi-hotel)' scarica un unico template con colonne hotel/numero/piano/tipologia/note. Una riga = una camera; la colonna hotel deve corrispondere al nome esatto di un hotel esistente (dropdown precompilato). Setup iniziale, aggiornamento globale o espansione gruppo in un'unica operazione. Limite 10.000 righe.",
                  "Import da HostFix (CSV): solo Super Admin. Se hai già configurato gli hotel su HostFix Pro, da /strutture bottone 'Importa da HostFix' carica direttamente il file CSV del gestionale (separatore ';', una riga per hotel, camere comma-separated nel campo 'camere'). Il sistema deduce il piano dal numero camera, riconosce gli hotel anche con denominazione leggermente diversa (match fuzzy su token) e popola contestualmente 'aree_comuni'. Pre-requisito: gli hotel devono già esistere a sistema.",
                ]} />
              </Card>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Limiti</div>
                <ProseList items={["File .xlsx massimo 10 MB", "Massimo 5.000 righe per file (oltre, splitta il file)"]} />
              </Card>
              <PermessiBlock rows={[
                ["Super Admin", "Importa hotel (qualsiasi) e staff (qualsiasi hotel)"],
                ["Resident / HK / FO Manager", "Importa staff solo per i propri hotel — le righe fuori scope sono marcate 'Permesso negato' nell'anteprima"],
                ["Capo Area / Read-only", "Non possono effettuare import"],
              ]} />
              <p className="text-sm text-muted-foreground"><strong>Tracciabilità</strong>: ogni operazione di import scrive una entry nel Registro attività (<code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">action=bulk_import</code>) con numero riga ed entità creata/aggiornata, consultabile dal Super Admin.</p>
            </div>
          </section>

          {/* 5 */}
          <section id="controlli">
            <SectionHeading n="06" icon={ClipboardList} title="Controlli giornalieri" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: ogni mattina alle 06:00 (ora italiana, Europe/Rome — CET/CEST automatico) il sistema genera automaticamente le task quotidiane per Resident Manager, Housekeeping Manager e Front Office Manager, basate sui template attivi. I controlli settimanali vengono generati il lunedì, quelli mensili il primo del mese.</p>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Controllo Camere 20%</div>
                <p className="text-sm">Il <strong>Resident Manager</strong> controlla ogni giorno un campione pari al <strong>20% delle camere totali</strong> dell'hotel. Es: hotel da 100 camere → 20 camere controllate/giorno. <strong>Ogni camera viene compilata singolarmente</strong> con una checklist dedicata.</p>
              </Card>
              <Card className="border-l-4 border-l-lavender-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-700 dark:text-lavender-300 mb-1.5">Gestione staff (super admin)</div>
                <p className="text-sm">In <em>Staff</em> il super admin può <strong>archiviare</strong> un dipendente con un click: viene rimosso dagli elenchi e dai KPI, ma valutazioni e stelle restano in archivio. Il toggle <strong>Archiviati</strong> mostra tutti gli archiviati con pulsante <strong>Ripristina</strong>.</p>
                <p className="text-sm mt-2">Per casi straordinari è disponibile anche <strong className="text-rose-600">Elimina definitivamente</strong> (solo nella tab Archiviati): l'azione cancella in modo <strong>irreversibile</strong> il dipendente e tutti i suoi dati collegati (valutazioni, stelle). Per attivare il bottone è obbligatorio digitare il cognome esatto nella modal di doppia conferma.</p>
              </Card>
              <Card className="border-l-4 border-l-lavender-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-700 dark:text-lavender-300 mb-1.5">Assenze Resident Manager</div>
                <p className="text-sm">In <em>Utenti</em> il super admin può registrare assenze (ferie / malattia / permesso / altro) di un Resident Manager con range di date e nota opzionale. Le task <strong>camere</strong> dei suoi hotel nel periodo vengono automaticamente <strong>annullate per assenza</strong> e non contano nelle metriche.</p>
                <p className="text-sm mt-2">Lo scheduler giornaliero salta automaticamente la generazione delle task camere se il RM è in assenza. È inoltre disponibile un pulsante <strong>Reset task in ritardo</strong> per ripulire eventuali task non eseguite arretrate.</p>
              </Card>
              <Card className="border-l-4 border-l-lavender-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-700 dark:text-lavender-300 mb-1.5">Task giornaliere multi-slot</div>
                <p className="text-sm">Un template giornaliero può essere configurato con <strong>più slot orari</strong> nella stessa giornata (max 4). Al momento sono attivi 4 template con slot <strong>Mattina 09:00 → 12:00</strong> e <strong>Pomeriggio 16:00 → 20:00</strong>: Controllo Bagni Comuni, Hall e Ordine Generale, Pulizia Reception, Presentazione Personale.</p>
                <ul className="text-sm mt-2 space-y-1 list-disc pl-5">
                  <li>Ogni slot genera una <strong>task indipendente</strong> con la sua card in <em>Controlli</em>, badge <span className="text-orange-600 font-medium">Mattina</span> / <span className="text-blue-600 font-medium">Pomeriggio</span> e ora di attivazione.</li>
                  <li><strong>Deadline per slot</strong>: superata l&apos;ora <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">deadline_ora</code> lo slot passa a <span className="text-amber-600 font-medium">In ritardo</span> (job scheduler ogni 15 minuti).</li>
                  <li><strong>Push in-app all&apos;ora dello slot</strong>: al minuto esatto dell&apos;ora slot il RM riceve una notifica interna con link diretto al controllo. La notifica viene <strong>skipata</strong> se il RM è in assenza o ha scelto un altro hotel nella DLA di oggi.</li>
                  <li>KPI performance: ogni slot conta come 1 task individuale (completamento e in-ritardo aggregati per singolo slot).</li>
                </ul>
                <p className="text-sm mt-2">Configurabile dal Super Admin in <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">/templates</code> → riga template → sezione &quot;Slot orari&quot; nel modal (drag&amp;drop label, ora HH:MM, deadline HH:MM). Template senza slot mantengono il comportamento single-slot classico (retrocompatibile).</p>
              </Card>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Tre valori per ogni voce di checklist</div>
                <ul className="text-sm space-y-1">
                  <li><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-2" /><strong>Conforme</strong>: standard rispettato</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-rose-500 mr-2" /><strong>Non Conforme</strong>: criticità rilevata; obbligo di nota</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-slate-400 mr-2" /><strong>N/A</strong>: voce non applicabile alla struttura (es. hotel senza piscina)</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-2">Le voci N/A contano come compilate ma <strong>non entrano</strong> nel calcolo del punteggio (denominatore ridotto). Un check con tutte voci N/A resta completabile ma senza punteggio numerico.</p>
              </Card>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Checklist per-camera</div>
                <p className="text-sm">Per ogni camera selezionata si apre una scheda dedicata con le voci configurate dal Super Admin in <em>Configurazione → Checklist Camera</em>. Le voci sono raggruppate in sezioni (es. Ingresso, Bagno, Camera), e ognuna può essere segnata come <strong>OK</strong>, <strong>Non OK</strong> (con nota di criticità) o <strong>N/A</strong>.</p>
                <p className="text-sm mt-2">A fine compilazione si assegna una valutazione complessiva alla camera: <strong>Perfetto</strong> (100), <strong>Buono</strong> (80), <strong>Sufficiente</strong> (60) o <strong>Insufficiente</strong> (30). Il <strong>punteggio del controllo giornaliero</strong> è la media dei punteggi delle camere compilate.</p>
              </Card>
              <Card className="border-l-4 border-l-lavender-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-700 dark:text-lavender-300 mb-1.5">Selezione libera & badge storici</div>
                <p className="text-sm">Alle 06:00 (Europe/Rome) il sistema pre-seleziona il <strong>20%</strong> di camere escludendo quelle controllate <strong>ieri</strong>. Il manager può liberamente <strong>aggiungere o rimuovere</strong> camere dalla lista. Ogni card mostra un badge storico:</p>
                <ul className="text-sm mt-2 space-y-1 list-disc pl-5">
                  <li><span className="text-rose-600 font-medium">Rosso "Controllata ieri"</span> — sconsigliata</li>
                  <li><span className="text-amber-600 font-medium">Arancione "Già questa settimana"</span> — controllata tra lunedì e ieri-1</li>
                  <li>Nessun badge — camera non controllata questa settimana</li>
                </ul>
                <p className="text-sm mt-2">Calcolo a caldo su settimana ISO (lunedì-domenica) → <strong>reset automatico ogni lunedì</strong>. Deselezionare una camera pre-selezionata permette di indicare un <strong>motivo opzionale</strong> come tag (DND, Cliente dentro, Manutenzione, Altro).</p>
                <p className="text-sm mt-2">La lista attiva delle camere del giorno è persistita sul task come <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">rooms_selected</code> e sostituisce integralmente lo snapshot iniziale <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">rooms_suggested</code> generato alle 06:00. Le camere fuori da <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">rooms_selected</code> non sono compilabili nella checklist.</p>
              </Card>
              <Card className="border-l-4 border-l-amber-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400 mb-1.5">Vincolo 20% con soft warning</div>
                <p className="text-sm">Al completa, se le camere selezionate sono <strong>meno del 20%</strong> minimo richiesto, il sistema mostra un avviso giallo che chiede conferma. Puoi completare comunque, ma la task viene marcata <code>sotto_soglia=true</code> con penalità nei KPI/classifiche.</p>
              </Card>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Esempi di controllo</h4>
                <ProseList items={[
                  "Pulizia reception, ordine hall, profumazione ambienti",
                  "Controllo buffet colazione, etichette allergeni",
                  "Pulizia bagni comuni, ascensori, camere campione",
                  "Pulizia corridoi e aree esterne",
                  "Presentazione personale e standard generale",
                ]} />
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Come si esegue un controllo</h4>
                <OrderedSteps items={[
                  "Vai in Controlli dal menu",
                  'Apri un controllo in stato "Da fare" e clicca "Esegui"',
                  "Spunta gli item della checklist e aggiungi note ai punti non conformi",
                  "Carica foto (facoltative) come evidenza",
                  "Seleziona la valutazione: Perfetto / Buono / Sufficiente / Insufficiente",
                  'Clicca "Completa controllo": il sistema genera la firma digitale con il tuo nome e timestamp',
                ]} />
              </div>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Stati possibili</div>
                <div className="text-sm">Da fare · Completato · Non eseguito · In ritardo</div>
              </Card>
              <PermessiBlock rows={[
                ["Resident Manager", "Esegue i controlli del proprio hotel"],
                ["HK Manager", "Esegue i controlli HK del proprio hotel"],
                ["FO Manager", "Esegue i controlli FO del proprio hotel"],
                ["Capo Area", "Visualizza i controlli di tutti gli hotel del cluster"],
                ["Super Admin", "Visualizza tutto + gestisce i template da /templates"],
              ]} />
            </div>
          </section>

          {/* 6 */}
          <section id="audit">
            <SectionHeading n="07" icon={ShieldCheck} title="Ispezioni Capo Area (ogni 60 giorni)" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: ogni 60 giorni il sistema genera automaticamente un'ispezione pianificata per ogni hotel e lo assegna al Capo Area di competenza. L'ispezione copre 11 categorie operative.</p>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Le 11 categorie ispezione</div>
                <div className="text-sm leading-7">Reception · Camere · Housekeeping · Colazione · Hall · Aree comuni · Bagni · Aree esterne · Presentazione personale · Back office · Locali tecnici</div>
              </Card>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Come si esegue</h4>
                <OrderedSteps items={[
                  "Vai in Ispezioni dal menu",
                  'Apri un\'ispezione "Pianificata" e clicca "Compila"',
                  "Per ognuna delle 11 categorie: spunta gli item della checklist, aggiungi commenti, foto, e assegna un punteggio da 1 a 10",
                  "I dati vengono salvati automaticamente ogni 1,2 secondi (autosave)",
                  "All'ultimo step vedi il punteggio totale calcolato live (media × 10 = 0-100) con semaforo",
                  'Clicca "Completa ispezione": viene generata la firma digitale, aggiornato lo storico dell\'hotel e calcolata la data della prossima ispezione (+60 giorni)',
                ]} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Card className="border-emerald-300/50 dark:border-emerald-700/40"><div className="text-[10px] uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400">≥ 80</div><div className="text-sm font-medium mt-1">Eccellente</div></Card>
                <Card className="border-amber-300/50 dark:border-amber-700/40"><div className="text-[10px] uppercase tracking-[0.16em] text-amber-600 dark:text-amber-400">60–79</div><div className="text-sm font-medium mt-1">Buono</div></Card>
                <Card className="border-rose-300/50 dark:border-rose-700/40"><div className="text-[10px] uppercase tracking-[0.16em] text-rose-600 dark:text-rose-400">&lt; 60</div><div className="text-sm font-medium mt-1">Critico</div></Card>
              </div>
              <PermessiBlock rows={[
                ["Capo Area", "Esegue le ispezioni dei propri hotel"],
                ["Super Admin", "Vede tutte le ispezioni, può crearne manualmente"],
                ["Altri ruoli operativi", "Vedono in sola lettura le ispezioni dei propri hotel"],
              ]} />
            </div>
          </section>

          {/* 7 */}
          <section id="performance">
            <SectionHeading n="08" icon={TrendingUp} title="Performance Resident Manager" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: dashboard dedicata al monitoraggio della performance individuale di ogni Resident Manager, con classifiche, trend e indicatori semaforici.</p>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Indicatori monitorati</h4>
                <ProseList items={[
                  "Task assegnate / completate / in ritardo / non eseguite",
                  "Controlli camere effettuati vs attesi",
                  "Tempo medio di completamento",
                  "Qualità di compilazione (% perfetto + buono)",
                  "Punteggio finale 0-100 (calcolato come 40% completion + 25% qualità + 20% puntualità + 15% controlli camere)",
                ]} />
              </div>
              <p><strong>Finestre temporali</strong>: 7 giorni · 30 giorni · 90 giorni</p>
              <Card>
                <div className="text-sm space-y-1">
                  <div>🥇🥈🥉 Top 3 Resident Manager del periodo</div>
                  <div>⚠️ Performance da migliorare</div>
                </div>
              </Card>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Come si usa</h4>
                <OrderedSteps items={[
                  "Vai in Performance dal menu",
                  "Seleziona la finestra temporale (7/30/90gg)",
                  "Visualizza i KPI aggregati e la classifica",
                  "Clicca su un RM per aprire il drawer di dettaglio con trend giornaliero e breakdown",
                ]} />
              </div>
              <PermessiBlock rows={[
                ["Super Admin", "Vede tutti i RM corporate"],
                ["Capo Area", "Vede solo i RM dei propri hotel"],
                ["Resident Manager", "Vede solo se stesso"],
              ]} />
            </div>
          </section>

          {/* 8 */}
          <section id="housekeeping">
            <SectionHeading n="09" icon={BedDouble} title="Produttività Housekeeping" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: monitora la produttività del reparto housekeeping con il KPI standard "camere pulite per ora lavorata".</p>
              <div className="grid grid-cols-3 gap-2">
                <Card className="border-emerald-300/50 dark:border-emerald-700/40"><div className="text-[10px] uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400">≥ 2,4 cam/h</div><div className="text-sm font-medium mt-1">Verde · target</div></Card>
                <Card className="border-amber-300/50 dark:border-amber-700/40"><div className="text-[10px] uppercase tracking-[0.16em] text-amber-600 dark:text-amber-400">2,0–2,4</div><div className="text-sm font-medium mt-1">Giallo · attenzione</div></Card>
                <Card className="border-rose-300/50 dark:border-rose-700/40"><div className="text-[10px] uppercase tracking-[0.16em] text-rose-600 dark:text-rose-400">&lt; 2,0</div><div className="text-sm font-medium mt-1">Rosso · critico</div></Card>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Come si inserisce un dato giornaliero</h4>
                <OrderedSteps items={[
                  "Vai in Housekeeping",
                  "Compila il form in alto: data, camere pulite, ore lavorate, numero risorse",
                  "Il sistema calcola in tempo reale la produttività con anteprima del semaforo",
                  "Salva: il dato entra nella tabella storica e nel grafico settimanale",
                ]} />
              </div>
              <Card className="border-l-4 border-l-rose-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400 mb-1.5">Correzioni (super admin)</div>
                <p className="text-sm">Se una riga è stata inserita per errore, il <strong>super admin</strong> può eliminarla cliccando sull'icona cestino nella tabella storica. L'eliminazione è <strong>irreversibile</strong> e gli aggregati (medie, classifiche, grafici, dashboard) vengono ricalcolati automaticamente.</p>
              </Card>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">La dashboard include</h4>
                <ProseList items={[
                  "Produttività media corrente",
                  "Confronto con la settimana precedente (delta %)",
                  "Bar chart trend settimanale (con linee target 2,4 e 2,0)",
                  "Classifica hotel per produttività",
                  "Hotel più produttivo del periodo",
                ]} />
              </div>
              <Card>
                <div className="text-sm"><strong>Anomaly detection</strong>: se un hotel scende sotto 2,0 per 2+ settimane consecutive, viene generato automaticamente un alert al Capo Area e all'HK Manager.</div>
              </Card>
              <PermessiBlock rows={[
                ["HK Manager", "Inserisce dati del proprio hotel"],
                ["Resident Manager", "Inserisce dati per il proprio hotel"],
                ["Capo Area", "Visualizza tutti gli hotel del cluster"],
                ["Super Admin", "Visualizza tutto + classifiche corporate"],
              ]} />
            </div>
          </section>

          {/* 9 */}
          <section id="staff">
            <SectionHeading n="10" icon={UserCheck} title="Valutazione Staff" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: gestione dell'anagrafica dipendenti e somministrazione di valutazioni periodiche con questionario dinamico configurabile.</p>
              <p><strong>Anagrafica dipendente</strong>: nome, cognome, età (opzionale, 0-99), ruolo, reparto, hotel, responsabile, data assunzione, storico valutazioni.</p>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Le 8 domande standard del questionario</h4>
                <OrderedSteps items={[
                  "Puntualità",
                  "Presentazione",
                  "Collaborazione",
                  "Precisione",
                  "Disponibilità",
                  "Qualità del lavoro",
                  "Attitudine ospite",
                  "Problem solving",
                ]} />
                <p className="text-sm text-muted-foreground mt-2">Ogni domanda viene valutata da 1 a 10 con possibilità di aggiungere un commento.</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Come si crea una valutazione</h4>
                <OrderedSteps items={[
                  "Vai in Staff, trova il dipendente da valutare (filtri per hotel/reparto/ruolo)",
                  "Apri la scheda dipendente",
                  'Clicca "Nuova valutazione" → wizard con tutti gli slider 1-10 e i commenti per ogni domanda attiva',
                  "Aggiungi un commento generale e salva",
                  "Il sistema calcola la media e aggiorna lo storico (con grafico)",
                ]} />
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5 flex items-center gap-1.5">Stelle obiettivi mensili</h4>
                <p className="text-sm text-muted-foreground">
                  Oltre alla valutazione periodica con questionario, il responsabile può assegnare a ciascun dipendente <strong>una stella al mese</strong> come riconoscimento per gli obiettivi raggiunti. Le stelle si accumulano nel tempo e sono visibili nel pannello del dipendente come <strong>timeline degli ultimi 12 mesi</strong> e come <strong>conteggio totale</strong>; un badge ⭐N appare anche nella lista Staff per i dipendenti premiati.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Solo il <strong>responsabile</strong> del dipendente (o un <strong>Super Admin</strong>) può assegnare o rimuovere stelle. Il vincolo è <strong>una stella per mese per dipendente</strong>: il sistema rifiuta i duplicati. È possibile l'assegnazione retroattiva su mesi passati.
                </p>
              </div>
              <p><strong>Configurazione del questionario</strong> (solo Super Admin): da <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">/configurazione/valutazioni</code> puoi aggiungere, modificare, disattivare o riordinare le domande.</p>
              <Card className="border-l-4 border-l-lavender-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-700 dark:text-lavender-300 mb-1.5">Modifica dipendente e trasferimento tra hotel</div>
                <p className="text-sm">Dal dettaglio dipendente (<code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">/staff/:id</code>) il bottone <strong>Modifica</strong> apre un modal per aggiornare anagrafica, ruolo, reparto, responsabile, data assunzione, email e telefono.</p>
                <ul className="text-sm mt-2 space-y-1 list-disc pl-5">
                  <li><strong>Super Admin</strong>: può cambiare qualsiasi hotel di destinazione.</li>
                  <li><strong>Resident Manager / Capo Area</strong>: può spostare un dipendente solo tra gli hotel a cui è assegnato (in caso di più hotel).</li>
                  <li><strong>Housekeeping / Front Office Manager</strong>: possono aggiornare anagrafica/ruolo/reparto ma <strong>non possono cambiare hotel</strong>.</li>
                </ul>
                <p className="text-sm mt-2">Ogni trasferimento viene tracciato nel campo <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">staff_move_history</code> con from/to hotel_id, timestamp e utente. Le <strong>valutazioni e le stelle storiche restano associate al dipendente</strong> anche dopo il cambio hotel: nessuna duplicazione, nessuna perdita.</p>
              </Card>
              <PermessiBlock rows={[
                ["Responsabile (RM/HK/FO)", "Valuta lo staff del proprio reparto/hotel"],
                ["Super Admin", "Configura il questionario e valuta chiunque; sposta i dipendenti tra qualsiasi hotel"],
                ["Read-only", "Visualizza schede e storico"],
              ]} />
            </div>
          </section>

          {/* 10 */}
          <section id="relazioni">
            <SectionHeading n="11" icon={FileText} title="Relazioni mensili obbligatorie" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: ogni Resident Manager compila una relazione mensile strutturata in <strong>10 sezioni</strong>. Ogni sezione ha un punteggio di completamento e la soglia di invio è del <strong>95% su tutte le 10 sezioni</strong>.</p>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Le 10 sezioni</h4>
                <ProseList items={[
                  "Generale — mese/anno, data compilazione, note interne (super admin)",
                  "Booking — punteggio, medie per aspetto, analisi + azioni correttive",
                  "Google — punteggio, recensioni, azioni per aumentare recensioni",
                  "Manutenzione — tecnici, ticket, ripartizione categoria, interventi sospesi",
                  "Transfer — venduti mese/YTD, obiettivi, criticità, azioni migliorative",
                  "Lavanderia — fornitore, stato servizio, problemi ricorrenti, follow-up",
                  "Housekeeping — stati operativi, organico, autonomia team, azioni",
                  "Front Office — stati reception/prenotazioni/reclami, valutazione finale",
                  "Breakfast — organico, media colazioni, incassi, profitto, azioni",
                  "Tabella Annuo — riepilogo 12 mesi (fatturato, food, transfer, booking, pulizia, google) + card Obiettivi YTD",
                ]} />
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Confronto vs obiettivi</h4>
                <p className="text-sm">I campi numerici sono confrontati automaticamente con gli obiettivi definiti in <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">/configurazione/obiettivi-hotel</code>. Il badge diventa <span className="text-emerald-500 font-medium">✓ verde</span> se il valore raggiunge il target, <span className="text-red-500 font-medium">✗ rosso</span> altrimenti. Metriche coperte: Booking, Pulizia Booking, Google, Transfer mese/annuo, Food mese/annuo.</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Tabella Annuo</h4>
                <ProseList items={[
                  "Riga del mese corrente della relazione: editabile inline (fatturato, food, transfer, booking, pulizia, google)",
                  "Mesi passati: read-only, precompilati automaticamente dalle relazioni già inviate dello stesso hotel/anno",
                  "Mesi futuri: read-only vuoti, stato \"non compilato\"",
                  "Sotto la tabella: card Obiettivi YTD con 6 KPI (Food, Transfer, Booking media, Pulizia Booking media, Google media, Fatturato) e delta % vs target",
                ]} />
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Allegati per sezione</h4>
                <p className="text-sm">Ogni sezione supporta l'upload di file (PDF, immagini, xlsx, csv — max 10 MB). Gli allegati sono elencati sotto il form della sezione e possono essere scaricati o rimossi (finché la relazione è in bozza).</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Flusso</h4>
                <OrderedSteps items={[
                  "Vai in Relazioni",
                  'Clicca "Nuova relazione" e seleziona hotel + mese + anno',
                  "Compila le 10 sezioni: clicca \"Salva sezione\" per persistere ogni sezione",
                  'Quando la barra "Compilazione media" raggiunge il 95% clicca "Invia relazione"',
                  "La relazione firmata diventa in sola lettura e alimenta la Tabella Annuo dei mesi successivi",
                ]} />
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Assistente AI</h4>
                <p className="text-sm">Ogni sezione (esclusa Tabella Annuo) ha il bottone <strong>&ldquo;Genera con AI&rdquo;</strong>. L&apos;AI legge i numeri inseriti + gli obiettivi hotel + la relazione del mese precedente e genera un paragrafo professionale di analisi (max 8 righe). Il testo è <strong>editabile</strong>: il manager può modificarlo prima dell&apos;invio.</p>
                <ProseList items={[
                  "L'AI si basa esclusivamente sui dati inseriti. Se un dato manca, scrive 'dato non disponibile'.",
                  "Confronta con il mese precedente (se già inviato) per evidenziare trend.",
                  "Evidenzia scostamenti da obiettivo (sopra/sotto target).",
                  "Non inventa eventi, cause o dettagli non presenti nei dati.",
                  "Il badge 'Bozza AI editabile' resta finché il manager non tocca il testo (poi diventa 'Modificato dal manager').",
                ]} />
                <p className="text-sm mt-2">Nella sezione Generale c&apos;è il bottone &ldquo;Genera conclusioni con AI&rdquo; che produce una sintesi finale (max 10-12 righe) con andamento generale, reparti performanti, criticità principali e priorità del mese successivo. Anche questo testo è editabile.</p>
                <p className="text-sm mt-2 italic text-muted-foreground">L&apos;AI è un supporto, non sostituisce il giudizio del manager. Rileggi sempre prima di inviare.</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Export PDF</h4>
                <p className="text-sm">Ogni relazione (bozza o inviata) può essere scaricata in PDF dal bottone <strong>&ldquo;Scarica PDF&rdquo;</strong> nell&apos;editor (in alto a destra) o dall&apos;icona di download nella lista. Il PDF include copertina con badge stato (BOZZA arancione / INVIATA verde), sommario KPI con delta vs target, le 10 sezioni complete, la Tabella Annuo e l&apos;elenco degli allegati (non embedded per motivi di dimensione).</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Feed &ldquo;Relazioni ricevute&rdquo; in Dashboard</h4>
                <p className="text-sm">Super Admin e Capo Area vedono un widget in Dashboard con le <strong>ultime 10 relazioni inviate</strong> (ordinate per data invio), ciascuna con: hotel, mese/anno, manager, timestamp relativo. Le relazioni non ancora aperte hanno il badge arancione <strong>Nuova</strong>. Se un KPI chiave (Booking, Pulizia Booking o Google) è sotto obiettivo target dell&apos;hotel/anno, appare l&apos;icona di attenzione rossa. Cliccando su una riga si apre la relazione e viene marcata come vista.</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Reminder automatici</h4>
                <ProseList items={[
                  "2 giorni prima della fine mese: reminder al RM",
                  'Primo giorno del mese successivo: alert "Relazione mancante" al Capo Area + Super Admin',
                  "All'invio: notifica automatica al Super Admin e al Capo Area dell'hotel",
                ]} />
              </div>
              <Card className="border-l-4 border-l-lavender-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-700 dark:text-lavender-300 mb-1.5">Schema dinamico RD1–RD4 (Super Admin)</div>
                <p className="text-sm">La struttura delle 10 sezioni è <strong>configurabile</strong> dal Super Admin da <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">/configurazione/schema-relazioni</code>. L&apos;editor supporta 4 tipi di campo (<strong>RD1</strong> numerico, <strong>RD2</strong> testo/textarea, <strong>RD3</strong> tabella a colonne configurabili, <strong>RD4</strong> array ripetibile con sotto-campi) e consente di <strong>trascinare (drag &amp; drop)</strong> sezioni e campi per riordinarli, rinominare voci, gestire colonne e sotto-campi dei widget speciali RD3/RD4 e attivare/disattivare i singoli campi.</p>
                <p className="text-sm mt-2">All&apos;invio di una relazione lo schema viene <strong>congelato</strong> in <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">schema_snapshot</code>: le modifiche successive allo schema globale <strong>non alterano</strong> le relazioni già inviate, che restano leggibili con la loro struttura originale (immutabilità garantita).</p>
              </Card>
              <PermessiBlock rows={[
                ["Resident Manager", "Crea, modifica (in bozza), invia le proprie relazioni"],
                ["Capo Area", 'Legge le relazioni dei propri hotel, vede l\'elenco "Mancanti del mese"'],
                ["Super Admin", "Tutto + modifica note_interne_direzione + configura obiettivi hotel + configura lo schema RD1–RD4"],
              ]} />
            </div>
          </section>

          {/* 12 — Daily Login Assignment */}
          <section id="daily">
            <SectionHeading n="12" icon={LogIn} title="Login giornaliero (Daily Login Assignment)" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: al primo accesso della giornata (fuso Europe/Rome) il sistema apre una modal obbligatoria in cui l&apos;utente dichiara come intende operare oggi. La scelta guida la generazione delle task giornaliere e il calcolo dei KPI.</p>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Le opzioni possibili</div>
                <ProseList items={[
                  "Operativo su un hotel — scegli l'hotel per cui lavorerai oggi. Solo per quell'hotel scattano i controlli giornalieri; le task degli altri hotel del cluster vengono automaticamente marcate come annullata_per_giornata_alt_hotel e non entrano nei KPI.",
                  "Assenza auto-dichiarata — riposo settimanale, ferie, malattia, permesso o altro (nota opzionale). Le task camere dell'utente per quella giornata vengono marcate annullata_per_assenza e non penalizzano le classifiche.",
                ]} />
              </Card>
              <Card className="border-l-4 border-l-amber-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400 mb-1.5">Scelta irreversibile</div>
                <p className="text-sm">Una volta confermata la scelta della giornata <strong>non è modificabile dall&apos;utente</strong>. La scelta resta valida fino alla mezzanotte Europe/Rome, poi la modal ricompare al successivo login. Solo il <strong>Super Admin</strong> può, in via eccezionale, cancellare la scelta di un utente per consentire una nuova dichiarazione dello stesso giorno (dal pannello <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">/admin/daily-assignments</code>).</p>
              </Card>
              <Card className="border-l-4 border-l-lavender-400">
                <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-700 dark:text-lavender-300 mb-1.5">Delega Temp RM per Front Office</div>
                <p className="text-sm">Il Super Admin può attivare, dalla scheda utente in <em>Utenti</em>, il flag <strong>Temp Resident Manager</strong> (<code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">temp_resident_manager</code>) su un Front Office Manager. Finché il flag è attivo, quel FO vede nella modal DLA anche gli hotel del cluster in cui deve sostituire un RM assente e può selezionarli come hotel operativo del giorno. Il flag è pensato per deleghe temporanee e si può disattivare in ogni momento.</p>
              </Card>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Come si compila</h4>
                <OrderedSteps items={[
                  'Effettua login: la modal "La tua giornata" appare automaticamente se non hai ancora dichiarato',
                  "Scegli operativo su un hotel oppure dichiara un'assenza",
                  "(Opzionale) aggiungi una nota per il Super Admin/Capo Area",
                  'Clicca "Conferma": la scelta viene registrata e la dashboard si popola con le sole task del contesto scelto',
                ]} />
              </div>
              <Card className="border-l-4 border-l-rose-500">
                <div className="text-[10px] uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400 mb-1.5">Reset selettivo &amp; Seed demo — Super Admin</div>
                <p className="text-sm">Dal menu <strong>Configurazione → Demo &amp; Reset</strong> (<code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">/configurazione/demo-tools</code>) sono disponibili due strumenti riservati al Super Admin:</p>
                <ul className="text-sm mt-2 space-y-1 list-disc pl-5">
                  <li><strong>Popola dati demo</strong>: crea relazioni V2 verosimili, daily check completati, produttività HK, staff con stelle e obiettivi annuali sui primi 4 hotel. Idempotente: richiamare non crea duplicati.</li>
                  <li><strong>Reset selettivo</strong>: cancella dati transazionali (relazioni, check, staff review, DLA, notifiche, ecc.) e hotel non in whitelist con cascade su camere e staff. Preserva Super Admin, template e schema. Richiede il testo di conferma esatto <code className="text-xs bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 px-1.5 rounded">RESET DEFINITIVO</code>.</li>
                  <li><strong>Anteprima dry-run</strong>: prima di eseguire il reset, il pulsante &quot;Anteprima&quot; mostra i counter esatti per collezione senza modificare nulla.</li>
                </ul>
                <p className="text-sm mt-2 text-rose-700 dark:text-rose-300">Il reset è <strong>totalmente distruttivo</strong>. Usa sempre l&apos;anteprima e verifica gli hotel selezionati prima di confermare.</p>
              </Card>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Notifica di malattia</div>
                <p className="text-sm">Se scegli <strong>Malattia</strong>, il sistema invia una notifica interna automatica al Super Admin e al Capo Area del cluster con flag <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">letto: false</code>. La notifica compare nel centro campanella in topbar e resta lì finché non viene marcata come letta.</p>
              </Card>
              <PermessiBlock rows={[
                ["Resident Manager", "Sceglie il proprio hotel o dichiara assenza"],
                ["HK / FO Manager", "Sceglie un hotel dell'assegnazione o dichiara assenza; FO con flag Temp RM vede anche gli hotel del cluster"],
                ["Capo Area", "Non compila DLA (nessun impatto sulle task giornaliere)"],
                ["Super Admin", "Non obbligato a compilare; consulta /admin/daily-assignments e può cancellare una scelta errata"],
              ]} />
            </div>
          </section>

          {/* 13 */}
          <section id="alert">
            <SectionHeading n="13" icon={AlertTriangle} title="Sistema Alert" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: motore di alert intelligente che monitora continuamente l'operatività e notifica i destinatari quando si verificano anomalie.</p>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Le 8 regole di default</h4>
                <OrderedSteps items={[
                  "Task camere non eseguita (3+ consecutive)",
                  "Task scaduta (controlli non eseguiti entro la giornata)",
                  "Controlli saltati consecutivi (2+ giorni di task non eseguite)",
                  "Performance RM sotto soglia (punteggio < 60 per 7 giorni)",
                  "Relazione mensile mancante (oltre la scadenza)",
                  "Produttività HK critica (< 2,0 per 2+ settimane consecutive)",
                  'Ispezione critica (ispezione completata con valutazione "critico")',
                  "Ispezione in ritardo (ispezione pianificata da più di 75 giorni non completata)",
                ]} />
              </div>
              <p><strong>Priorità</strong>: Bassa · Media · Alta · Critica</p>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Canali</div>
                <ul className="text-sm space-y-1">
                  <li>✅ <strong>In-app</strong> (centro notifiche, attivo)</li>
                  <li>🚧 Email — disponibile dalla prossima release</li>
                  <li>🚧 Push browser — disponibile dalla prossima release</li>
                </ul>
              </Card>
              <p><strong>Configurazione regole</strong> (Super Admin): da <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">/configurazione/alert</code> puoi attivare/disattivare regole, modificare soglie, scegliere ruoli destinatari e canali.</p>
              <p><strong>Centro notifiche in-app</strong>: icona campanella in topbar con badge dei messaggi non letti, dropdown rapido con le ultime notifiche, polling automatico ogni 30 secondi.</p>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Mark as read &amp; badge non letto</div>
                <p className="text-sm">Ogni notifica ha il flag booleano <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">letto</code> (default <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">false</code>). Il badge sull'icona campanella conta esclusivamente le notifiche con <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">letto: false</code>. Aprire il dropdown o cliccare su una singola notifica la marca come letta e aggiorna il contatore in tempo reale.</p>
                <p className="text-sm mt-2">Le notifiche sono filtrate per ruolo e cluster: RM/HK/FO ricevono solo eventi del proprio hotel, il Capo Area quelli del proprio cluster, il Super Admin l'intero gruppo. Le notifiche automatiche generate da eventi DLA (es. malattia dichiarata) e dagli alert configurati confluiscono nello stesso centro.</p>
              </Card>
              <PermessiBlock rows={[
                ["Super Admin", "Configura regole, riceve tutti gli alert critici"],
                ["Capo Area", "Riceve alert relativi al proprio cluster"],
                ["Manager operativi", "Ricevono alert relativi al proprio ruolo/hotel"],
                ["Tutti gli utenti", "Hanno il proprio centro notifiche in-app"],
              ]} />
            </div>
          </section>

          {/* 14 */}
          <section id="report">
            <SectionHeading n="14" icon={Download} title="Report PDF & Excel" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: generazione on-demand di report in formato PDF e Excel con branding IH AuditFlow.</p>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Tipi di report disponibili</h4>
                <OrderedSteps items={[
                  "Report mensile hotel — KPI completi di un hotel per un mese specifico",
                  "Report trimestrale corporate — riepilogo aggregato di tutti gli hotel su 3 mesi",
                  "Confronto strutture — confronto side-by-side tra hotel selezionati",
                  "Classifica corporate — top e bottom hotel per qualità, performance, produttività",
                  "Report Resident Manager — performance individuale dettagliata",
                  "Report ispezione — dettaglio completo di un'ispezione con punteggi, commenti, foto e firma",
                ]} />
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Come si genera</h4>
                <OrderedSteps items={[
                  "Vai in Report dal menu",
                  "Scegli il tipo di report e clicca la card",
                  "Compila i parametri (hotel, periodo, formato)",
                  'Clicca "Genera": il file viene scaricato automaticamente',
                ]} />
              </div>
              <p>Pulsanti di esportazione rapida sono disponibili anche su <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">/strutture/:id</code> e sulle ispezioni completate.</p>
              <PermessiBlock rows={[
                ["Tutti i ruoli", "Esportano solo i report relativi al proprio scope (capo_area su cluster, RM su proprio hotel, ecc.)"],
              ]} />
            </div>
          </section>

          {/* 15 */}
          <section id="ai">
            <SectionHeading n="15" icon={Sparkles} title="AI Assistant" />
            <div className="space-y-4 text-[15px] text-foreground/85">
              <p><strong>Cosa fa</strong>: assistente AI integrato che analizza i dati corporate e fornisce riepiloghi, anomalie e insight in linguaggio naturale.</p>
              <div>
                <h4 className="font-medium text-foreground mb-1.5">Funzionalità</h4>
                <ProseList items={[
                  'Chat conversazionale (icona ✨ in topbar): chiedi in italiano domande come "qual è il RM con peggior trend?" o "riassumi le criticità del giorno"',
                  "Riepilogo settimanale hotel: widget nella pagina di dettaglio hotel che genera un sintetico stato della struttura",
                  'Anomaly briefing: widget nella dashboard con il riepilogo di "cosa richiede attenzione adesso"',
                  "Executive summary ispezione: widget sulle ispezioni completate che produce un sommario per il management",
                ]} />
              </div>
              <Card>
                <div className="text-sm"><strong>Modello utilizzato</strong>: Gemini 3 Flash via Emergent LLM Key. Cache delle risposte 24 ore per ottimizzare i costi. Rate limit di 30 chiamate/ora per utenti non Super Admin.</div>
              </Card>
              <p><strong>Configurazione</strong> (Super Admin): da <code className="text-xs bg-accent/40 px-1.5 py-0.5 rounded">/configurazione/ai</code> puoi monitorare lo stato del provider, svuotare la cache, attivare/disattivare l'AI globalmente.</p>
              <p><strong>Privacy</strong>: nessun dato sensibile esce dalla piattaforma se non strettamente necessario per generare l'analisi. Tutte le chiamate AI sono loggate nel registro attività.</p>
              <PermessiBlock rows={[
                ["Tutti gli utenti", "Usano la chat e i widget contestuali (scope filtrato per ruolo)"],
                ["Super Admin", "Gestisce la configurazione AI"],
              ]} />
            </div>
          </section>

          {/* 16 Matrice */}
          <section id="matrice">
            <SectionHeading n="16" icon={TableIcon} title='Matrice "Chi può fare cosa"' />
            <div className="rounded-2xl border border-border/60 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background/95 backdrop-blur">
                    <TableRow>
                      <TableHead className="min-w-[220px] text-xs">Azione</TableHead>
                      <TableHead className="text-center text-xs">Super Admin</TableHead>
                      <TableHead className="text-center text-xs">Capo Area</TableHead>
                      <TableHead className="text-center text-xs">Resident Manager</TableHead>
                      <TableHead className="text-center text-xs">HK Manager</TableHead>
                      <TableHead className="text-center text-xs">FO Manager</TableHead>
                      <TableHead className="text-center text-xs">Read-only</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      ["Creare/modificare hotel", "✅", "—", "—", "—", "—", "—"],
                      ["Gestire utenti", "✅", "—", "—", "—", "—", "—"],
                      ["Creare template checklist", "✅", "—", "—", "—", "—", "—"],
                      ["Eseguire controlli giornalieri", "✅", "—", "✅", "✅", "✅", "—"],
                      ["Visualizzare controlli", "✅", "✅ cluster", "✅ proprio hotel", "✅ proprio hotel", "✅ proprio hotel", "👁️"],
                      ["Eseguire ispezioni 60gg", "✅", "✅ cluster", "—", "—", "—", "—"],
                      ["Visualizzare ispezioni", "✅", "✅ cluster", "👁️ proprio", "👁️ proprio", "👁️ proprio", "👁️"],
                      ["Dashboard performance RM", "✅ tutti", "✅ cluster", "👁️ proprio", "—", "—", "👁️"],
                      ["Inserire produttività HK", "✅", "—", "✅ proprio", "✅ proprio", "—", "—"],
                      ["Compilare relazione mensile", "—", "—", "✅ proprio hotel", "—", "—", "—"],
                      ["Vedere relazioni mensili", "✅ tutte", "✅ cluster", "👁️ proprie", "—", "—", "👁️"],
                      ["Configurare regole alert", "✅", "—", "—", "—", "—", "—"],
                      ["Gestire questionario staff", "✅", "—", "—", "—", "—", "—"],
                      ["Valutare staff", "✅", "—", "✅ proprio team", "✅ proprio team", "✅ proprio team", "—"],
                      ["Generare report PDF/Excel", "✅", "✅ cluster", "✅ proprio hotel", "✅ proprio hotel", "✅ proprio hotel", "✅ scope"],
                      ["AI Chat", "✅ corporate", "✅ cluster", "✅ proprio hotel", "✅ proprio hotel", "✅ proprio hotel", "✅"],
                      ["Configurare AI", "✅", "—", "—", "—", "—", "—"],
                      ["Registro attività", "✅", "—", "—", "—", "—", "—"],
                      ["Importare hotel da Excel", "✅", "—", "—", "—", "—", "—"],
                      ["Importare staff da Excel", "✅", "—", "✅ propri hotel", "✅ propri hotel", "✅ propri hotel", "—"],
                    ].map((row, i) => (
                      <TableRow key={i} className="text-sm">
                        <TableCell className="font-medium">{row[0]}</TableCell>
                        {row.slice(1).map((cell, j) => (
                          <TableCell key={j} className="text-center text-xs whitespace-nowrap text-foreground/80">{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Legenda: ✅ permesso · 👁️ sola lettura · — non permesso</p>
          </section>

          {/* 15 FAQ */}
          <section id="faq">
            <SectionHeading n="17" icon={HelpCircle} title="FAQ" />
            <Accordion type="single" collapsible className="space-y-2" data-testid="guida-faq">
              {[
                {
                  q: "Posso caricare più strutture o dipendenti tutti insieme?",
                  a: "Sì. In Strutture o Staff trovi il pulsante \"Importa da Excel\" che permette di scaricare un template precompilato, riempirlo con i tuoi dati e ricaricarlo. Il sistema mostra un'anteprima con validazione riga per riga prima di eseguire l'import definitivo.",
                },
                {
                  q: "Cosa succede se il Resident Manager è in riposo o ferie?",
                  a: "Il RM può dichiarare un giorno di riposo dal proprio Profilo (anche retroattivo, fino a 7 giorni indietro, o pianificato fino a 7 giorni avanti). Le task di quel giorno vengono contrassegnate come \"Riposo\" e NON entrano nel calcolo delle performance. Motivi disponibili: riposo settimanale, ferie, malattia, altro.",
                },
                {
                  q: "Posso modificare un controllo dopo averlo completato?",
                  a: "No. Una volta firmato, il controllo diventa parte dello storico immutabile. Solo Super Admin può eccezionalmente intervenire per correggere errori critici.",
                },
                {
                  q: "Le foto sui controlli giornalieri sono obbligatorie?",
                  a: "No, sono facoltative. È buona prassi caricarle come evidenza, ma il completamento del controllo non è bloccato dalla loro assenza.",
                },
                {
                  q: "Cosa succede se non invio la relazione mensile entro la scadenza?",
                  a: 'Il sistema genera automaticamente un alert "Relazione mancante" al Capo Area e al Super Admin il primo giorno del mese successivo.',
                },
                {
                  q: "Come viene calcolato il punteggio Performance del Resident Manager?",
                  a: "Con questa formula: 40% tasso di completamento task (completate/assegnate), 25% qualità (% valutazioni perfetto + buono), 20% puntualità (1 − in ritardo/assegnate), 15% controlli camere effettuati/attesi. Il risultato è normalizzato 0-100 con semaforo a 80 (verde) e 60 (giallo).",
                },
                {
                  q: "Le notifiche arrivano anche via email?",
                  a: "Al momento solo in-app. I canali email e push sono predisposti nella configurazione ma saranno attivati in una release futura.",
                },
                {
                  q: "L'AI Assistant può vedere i miei dati personali?",
                  a: "L'AI riceve solo aggregati operativi (KPI, conteggi, trend) necessari a generare le analisi. Nessun dato personale identificativo o credenziale viene mai inviato al modello.",
                },
                {
                  q: "Posso installare IH AuditFlow come app sul mio telefono?",
                  a: 'Sì. La piattaforma è una PWA installabile con icona "Wave Flow" (simbolo lavanda di IH AuditFlow). Da Chrome o Edge (desktop o Android) appare automaticamente il prompt "Installa app". Da iPhone/iPad usa Safari → Condividi → "Aggiungi a schermata Home".',
                },
                {
                  q: "Cosa succede se perdo la connessione internet?",
                  a: "Le pagine già caricate restano consultabili grazie al service worker PWA. Nuove operazioni (es. invio controlli) richiedono di tornare online.",
                },
              ].map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl border border-border/60 bg-background/40 px-4">
                  <AccordionTrigger className="text-sm text-left hover:no-underline" data-testid={`faq-trigger-${i}`}>{item.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-foreground/80 leading-[1.75]">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* 18 Glossario */}
          <section id="glossario">
            <SectionHeading n="18" icon={Library} title="Glossario" />
            <dl className="grid sm:grid-cols-2 gap-3 text-sm">
              {[
                ["RM", "Resident Manager, responsabile operativo dell'hotel"],
                ["HK", "Housekeeping, reparto pulizia camere e aree comuni"],
                ["FO", "Front Office, reception e accoglienza ospiti"],
                ["Ispezione", "Verifica completa eseguita dal Capo Area ogni 60 giorni"],
                ["Task", "Singola attività di controllo assegnata automaticamente al ruolo target"],
                ["KPI", "Key Performance Indicator, indicatore numerico di performance"],
                ["Punteggio qualità", "Valore 0-100 calcolato dalle ispezioni, con semaforo verde/giallo/rosso"],
                ["Anomaly detection", "Rilevamento automatico di pattern anomali (es. produttività HK in calo da 2+ settimane)"],
                ["Centro notifiche", "Centro messaggi in-app accessibile dall'icona campanella in topbar"],
                ["PWA", "Progressive Web App, applicazione web installabile come app nativa"],
                ["Wave Flow", "Nome dell'icona PWA di IH AuditFlow (simbolo lavanda), usato come app icon nell'installazione mobile/desktop"],
                ["DLA", "Daily Login Assignment, la scelta obbligatoria (hotel operativo o assenza) che ogni utente operativo dichiara al primo login della giornata"],
                ["RD1–RD4", "Tipologie di campo dello schema Relazioni Mensili: RD1 numerico, RD2 testo, RD3 tabella a colonne configurabili, RD4 array ripetibile con sotto-campi"],
                ["Snapshot schema", "Copia congelata dello schema Relazioni al momento dell'invio (campo schema_snapshot); garantisce che le relazioni inviate restino leggibili anche se lo schema globale cambia"],
                ["rooms_selected", "Lista attiva delle camere selezionate per il Controllo Camere della giornata; sostituisce lo snapshot rooms_suggested generato dallo scheduler alle 06:00"],
                ["temp_resident_manager", "Flag booleano assegnabile a un Front Office Manager per delegargli temporaneamente le mansioni RM in caso di sostituzione"],
              ].map(([term, def]) => (
                <Card key={term}>
                  <dt className="text-lavender-700 dark:text-lavender-300 font-semibold text-sm">{term}</dt>
                  <dd className="text-foreground/80 text-sm mt-1">{def}</dd>
                </Card>
              ))}
            </dl>
          </section>

          {/* Footer */}
          <footer className="pt-8 mt-8 border-t border-border/40 text-xs text-muted-foreground space-y-1">
            <div>Versione manuale: aggiornato al {buildDate}.</div>
            <div>Hai bisogno di aiuto? Contatta la sede Invest Hospitality.</div>
          </footer>
        </main>
      </div>
    </div>
  );
}
